/**
 * Admin tab: Video Reference Assets
 * Master-only. Manages shared font and music assets used by Video Reference Ads.
 * API contract:
 *   GET  /api/admin/video-reference-assets         → all assets (+ isActive, archivedAt, sha256, createdAt)
 *   POST /api/admin/video-reference-assets/import-font  { label, fontUrl }
 *   POST /api/admin/video-reference-assets/import       multipart: music asset + label
 *   PATCH /api/admin/video-reference-assets/:id    { label?, isActive? }
 *   DELETE /api/admin/video-reference-assets/:id   archives
 */
import { useEffect, useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Type, Music, Upload, Copy, Check, Loader2, Trash2, RefreshCw,
  FileAudio, AlignLeft, AlignCenter, AlignRight, Eye, EyeOff,
  Globe2, ChevronsUpDown,
} from "lucide-react";
import { GOOGLE_FONT_CATALOG } from "@shared/google-fonts";
import {
  normalizeVideoReferenceWorkflowResponse,
  VIDEO_REFERENCE_PROMPT_TYPES,
  type VideoReferencePromptType,
  type VideoReferenceWorkflowResponse,
  type VideoReferenceWorkflowSetting,
} from "@/lib/video-reference-workflow-response";

export interface VideoReferenceAsset {
  id: string;
  kind: "font" | "music";
  label: string;
  fileName: string;
  format: string;
  /** CSS font-family name (fonts only) */
  fontFamily?: string | null;
  /** Duration in seconds (music only) */
  durationSec?: number | null;
  sizeBytes?: number | null;
  previewUrl: string;
  isActive: boolean;
  archivedAt?: string | null;
  sha256?: string | null;
  createdAt?: string | null;
}

const ASSETS_KEY = "/api/admin/video-reference-assets";
const WORKFLOWS_KEY = "/api/admin/video-reference-workflows";

function validateWorkflowUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    const host = url.hostname.toLowerCase();
    if (
      url.protocol !== "https:" ||
      !!url.username ||
      !!url.password ||
      !!url.port ||
      !(host === "sim.ai" || host.endsWith(".sim.ai")) ||
      !/^\/api\/(?:v\d+\/)?workflows\/[^/]+\/execute\/?$/.test(url.pathname)
    ) {
      return "Enter a usable HTTPS Sim workflow execute URL on an allowed sim.ai host.";
    }
    return null;
  } catch {
    return "Enter a valid URL.";
  }
}

function normalizeOptionalWorkflowUrl(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed || null;
}

function workflowSettingsUrl(type: VideoReferencePromptType) {
  return `${WORKFLOWS_KEY}?type=${encodeURIComponent(type)}`;
}

function workflowSettingsQueryKey(type: VideoReferencePromptType) {
  return [WORKFLOWS_KEY, type];
}

function WorkflowSettingRow({
  setting,
  promptType,
}: {
  setting: VideoReferenceWorkflowSetting;
  promptType: VideoReferencePromptType;
}) {
  const [rowError, setRowError] = useState<string | null>(null);
  const [workflowUrl, setWorkflowUrl] = useState(setting.videoPromptVariantEditorSimUrl ?? "");
  const currentPromptVersion = setting.currentPromptVersions?.[promptType]
    ?? (promptType === "general" && !setting.currentPromptVersions
      && setting.currentPromptVersionId && setting.currentPromptVersionNumber
      ? {
          id: setting.currentPromptVersionId,
          versionNumber: setting.currentPromptVersionNumber,
        }
      : null);

  useEffect(() => {
    setWorkflowUrl(setting.videoPromptVariantEditorSimUrl ?? "");
  }, [setting.videoPromptVariantEditorSimUrl, promptType]);

  const disable = useMutation({
    mutationFn: () => apiRequest("DELETE", `${WORKFLOWS_KEY}/${setting.brandId}`, { type: promptType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_KEY] });
      queryClient.invalidateQueries({
        queryKey: ["/api/brands", setting.brandId, "video-reference-ads", "availability"],
      });
    },
    onError: (error: Error) => setRowError(error.message),
  });

  const updateWorkflow = useMutation({
    mutationFn: async () => {
      const normalizedUrl = normalizeOptionalWorkflowUrl(workflowUrl);
      const validationError = normalizedUrl ? validateWorkflowUrl(normalizedUrl) : null;
      if (validationError) throw new Error(validationError);
      const res = await apiRequest("PATCH", `${WORKFLOWS_KEY}/${setting.brandId}`, {
        type: promptType,
        videoPromptVariantEditorSimUrl: normalizedUrl,
      });
      return res.json();
    },
    onSuccess: () => {
      setRowError(null);
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_KEY] });
      queryClient.invalidateQueries({
        queryKey: ["/api/brands", setting.brandId, "video-reference-ads", "availability"],
      });
    },
    onError: (error: Error) => setRowError(error.message),
  });

  return (
    <>
      <div className="rounded-md border p-3" data-testid={`card-vra-workflow-${setting.brandId}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">{setting.brandName}</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge variant="default">{promptType === "general" ? "Enabled" : "General access enabled"}</Badge>
              <Badge
                variant={currentPromptVersion ? "secondary" : "outline"}
                className="text-[10px]"
                data-testid={`badge-vra-prompt-status-${setting.brandId}-${promptType}`}
              >
                {promptType === "3d" ? "3D" : promptType.toUpperCase()} prompt {currentPromptVersion
                  ? `v${currentPromptVersion.versionNumber}`
                  : "not set"}
              </Badge>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {promptType === "general" && <Button type="button" size="sm" variant="destructive" onClick={() => disable.mutate()}
              disabled={disable.isPending || updateWorkflow.isPending}
              data-testid={`button-disable-vra-workflow-${setting.brandId}`}>
              {disable.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Disable"}
            </Button>}
          </div>
        </div>

        <form
          className="mt-3 rounded-md border bg-muted/20 p-2.5"
          onSubmit={(event) => {
            event.preventDefault();
            setRowError(null);
            updateWorkflow.mutate();
          }}
          data-testid={`form-vra-sim-fallback-${setting.brandId}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-xs font-medium">Sim fallback (optional)</p>
              <p className="text-[11px] text-muted-foreground">
                {currentPromptVersion
                  ? `Secondary workflow for ${promptType === "3d" ? "3D" : promptType.toUpperCase()} runs without a saved ${promptType === "3d" ? "3D" : promptType.toUpperCase()} prompt.`
                  : `Optional secondary workflow for ${promptType === "3d" ? "3D" : promptType.toUpperCase()} runs.`}
              </p>
            </div>
            {setting.canUseSimFallback ? (
              <Badge variant="secondary" className="text-[10px]">Available</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px]">Not configured</Badge>
            )}
          </div>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <div className="min-w-0 flex-1">
              <Label htmlFor={`vra-workflow-url-${setting.brandId}`} className="sr-only">
                Sim fallback workflow URL for {setting.brandName} ({promptType})
              </Label>
              <Input
                id={`vra-workflow-url-${setting.brandId}`}
                type="url"
                value={workflowUrl}
                onChange={(event) => {
                  setWorkflowUrl(event.target.value);
                  setRowError(null);
                }}
                placeholder="https://www.sim.ai/api/v2/workflows/…/execute"
                maxLength={2048}
                disabled={updateWorkflow.isPending || disable.isPending}
              data-testid={`input-vra-workflow-url-${setting.brandId}`}
              />
            </div>
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={updateWorkflow.isPending || disable.isPending}
              data-testid={`button-save-vra-workflow-url-${setting.brandId}`}
            >
              {updateWorkflow.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save URL"}
            </Button>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Leave blank to keep the {promptType === "3d" ? "3D" : promptType.toUpperCase()} Sim fallback unconfigured.
          </p>
        </form>

        {rowError && (
          <p className="mt-2 text-xs text-destructive" role="alert" data-testid={`error-vra-workflow-${setting.brandId}`}>
            {rowError}
          </p>
        )}
      </div>
    </>
  );
}

function WorkflowSettingsPanel() {
  const [promptType, setPromptType] = useState<VideoReferencePromptType>("general");
  const { data, isLoading, error, refetch, isFetching } = useQuery<VideoReferenceWorkflowResponse>({
    queryKey: workflowSettingsQueryKey(promptType),
    queryFn: async () => {
      const res = await apiRequest("GET", workflowSettingsUrl(promptType));
      return normalizeVideoReferenceWorkflowResponse(await res.json());
    },
  });
  const [brandId, setBrandId] = useState("");
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [workflowUrl, setWorkflowUrl] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const selectedBrand = data?.availableBrands.find((brand) => brand.brandId === brandId);
  const enable = useMutation({
    mutationFn: async () => {
      if (!brandId) throw new Error("Choose an active brand.");
      const normalizedUrl = normalizeOptionalWorkflowUrl(workflowUrl);
      const validationError = normalizedUrl ? validateWorkflowUrl(normalizedUrl) : null;
      if (validationError) throw new Error(validationError);
      const res = await apiRequest("PATCH", `${WORKFLOWS_KEY}/${brandId}`, {
        type: promptType,
        ...(normalizedUrl ? { videoPromptVariantEditorSimUrl: normalizedUrl } : {}),
      });
      return res.json();
    },
    onSuccess: () => {
      const enabledBrandId = brandId;
      setBrandId("");
      setWorkflowUrl("");
      setFormError(null);
      queryClient.invalidateQueries({ queryKey: [WORKFLOWS_KEY] });
      queryClient.invalidateQueries({
        queryKey: ["/api/brands", enabledBrandId, "video-reference-ads", "availability"],
      });
    },
    onError: (error: Error) => setFormError(error.message),
  });

  return (
    <section className="space-y-3" aria-labelledby="vra-workflow-settings-title">
      <div>
        <h3 id="vra-workflow-settings-title" className="flex items-center gap-2 text-base font-medium">
          <Globe2 className="h-4 w-4" />
          Video Reference Ads access
        </h3>
        <p className="text-sm text-muted-foreground">
          Manage brand access and optional Sim fallback workflows here. Edit prompts on the Video Reference Ads page.
        </p>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading client workflows…
        </div>
      ) : error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm space-y-2">
          <p className="font-medium text-destructive">Failed to load client workflows</p>
          <p className="text-xs text-muted-foreground">{(error as Error).message}</p>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Retrying…" : "Retry"}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <Tabs
            value={promptType}
            onValueChange={(value) => setPromptType(value as VideoReferencePromptType)}
            data-testid="tabs-admin-vra-prompt-type"
          >
            <TabsList aria-label="Workflow type">
              {VIDEO_REFERENCE_PROMPT_TYPES.map((type) => (
                <TabsTrigger key={type} value={type} data-testid={`tab-admin-vra-prompt-${type}`}>
                  {type === "3d" ? "3D" : type === "ugc" ? "UGC" : "General"}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {promptType !== "general" && (
            <p className="text-xs text-muted-foreground">
              These brands have General access. UGC and 3D fallback URLs can be prepared independently here; access is enabled or disabled on the General tab.
            </p>
          )}
          {promptType === "general" && <form className="grid gap-3 rounded-md border bg-muted/20 p-3 md:grid-cols-[minmax(180px,0.8fr)_minmax(280px,2fr)_auto] md:items-end"
            onSubmit={(event) => { event.preventDefault(); setFormError(null); enable.mutate(); }}
            data-testid="form-enable-vra-brand">
            <div className="space-y-1.5">
              <Label htmlFor="vra-enable-brand" className="text-xs">Active brand</Label>
              <Popover open={brandPickerOpen} onOpenChange={setBrandPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id="vra-enable-brand"
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={brandPickerOpen}
                    aria-haspopup="listbox"
                    disabled={enable.isPending}
                    className="h-9 w-full justify-between bg-background px-3 text-sm font-normal"
                    data-testid="select-enable-vra-brand"
                  >
                    <span className="min-w-0 truncate text-left">
                      {selectedBrand?.brandName ?? "Choose a brand…"}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[var(--radix-popover-trigger-width)] min-w-[18rem] p-0"
                  data-testid="popover-enable-vra-brand"
                >
                  <Command>
                    <CommandInput placeholder="Search active brands…" data-testid="input-search-enable-vra-brand" />
                    <CommandList className="max-h-72">
                      <CommandEmpty>No active brands found.</CommandEmpty>
                      <CommandGroup heading="Active brands">
                        {data?.availableBrands.map((brand) => (
                          <CommandItem
                            key={brand.brandId}
                            value={`${brand.brandName} ${brand.brandId}`}
                            onSelect={() => {
                              setBrandId(brand.brandId);
                              setFormError(null);
                              setBrandPickerOpen(false);
                            }}
                            data-testid={`option-enable-vra-brand-${brand.brandId}`}
                          >
                            <Check
                              className={[
                                "h-4 w-4",
                                brandId === brand.brandId ? "opacity-100" : "opacity-0",
                              ].join(" ")}
                            />
                            <span className="truncate">{brand.brandName}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
                <Label htmlFor="vra-enable-url" className="text-xs">
                  General Sim fallback URL <span className="text-muted-foreground">(optional)</span>
                </Label>
              <Input id="vra-enable-url" value={workflowUrl}
                onChange={(event) => { setWorkflowUrl(event.target.value); setFormError(null); }}
                placeholder="https://www.sim.ai/api/v2/workflows/…/execute"
                disabled={enable.isPending} data-testid="input-enable-vra-workflow" />
            </div>
             <Button type="submit" size="sm" disabled={enable.isPending || !brandId}
              data-testid="button-enable-vra-brand">
              {enable.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Enable brand"}
            </Button>
            {formError && <p className="text-xs text-destructive md:col-span-3" role="alert">{formError}</p>}
          </form>}
          <div className="space-y-2">
            {(data?.enabled.length ?? 0) === 0 && (
              <p className="text-sm text-muted-foreground">No brands are enabled for {promptType === "3d" ? "3D" : promptType.toUpperCase()} prompts.</p>
            )}
            {data?.enabled.map((setting) => (
            <WorkflowSettingRow
              key={setting.brandId}
              setting={setting}
              promptType={promptType}
            />
          ))}
          </div>
        </div>
      )}
    </section>
  );
}

function formatBytes(n: number | null | undefined): string {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatDuration(sec: number | null | undefined): string {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function CopyIdButton({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      title="Copy asset ID"
      aria-label="Copy asset ID"
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      data-testid={`button-copy-asset-id-${id}`}
    >
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
      {id.slice(0, 8)}…
    </button>
  );
}

function FontSpecimen({ asset }: { asset: VideoReferenceAsset }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!asset.previewUrl) return;
    const styleId = `vra-font-${asset.id}`;
    if (!document.getElementById(styleId)) {
      const src = asset.previewUrl;
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `@font-face { font-family: 'vra-${asset.id}'; src: url('${src}'); }`;
      document.head.appendChild(style);
    }
    document.fonts?.load(`16px "vra-${asset.id}"`).catch(() => setFailed(true));
  }, [asset.id, asset.previewUrl]);

  const cssFamily = `'vra-${asset.id}', sans-serif`;

  return (
    <div
      className="mt-1 overflow-hidden rounded bg-muted/30 px-3 py-1.5 text-sm leading-snug"
      style={{ fontFamily: failed ? undefined : cssFamily }}
      aria-label="Font specimen"
    >
      Aa Bb Cc 0123
    </div>
  );
}

function AudioPreview({ asset }: { asset: VideoReferenceAsset }) {
  return (
    <audio
      src={asset.previewUrl}
      controls
      preload="none"
      className="mt-1 h-8 w-full"
      aria-label={`Preview ${asset.label}`}
    />
  );
}

function AssetCard({
  asset,
  onArchive,
  archiving,
}: {
  asset: VideoReferenceAsset;
  onArchive: (id: string) => void;
  archiving: boolean;
}) {
  const { toast } = useToast();

  const patch = useMutation({
    mutationFn: async (body: { label?: string; isActive?: boolean }) => {
      const res = await apiRequest("PATCH", `${ASSETS_KEY}/${asset.id}`, body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ASSETS_KEY] });
    },
    onError: (e: Error) => {
      toast({ title: "Update failed", description: e.message, variant: "destructive" });
    },
  });

  const [editingLabel, setEditingLabel] = useState(false);
  const [labelValue, setLabelValue] = useState(asset.label);
  const labelInputRef = useRef<HTMLInputElement>(null);

  const saveLabel = () => {
    const trimmed = labelValue.trim();
    if (!trimmed) { setLabelValue(asset.label); setEditingLabel(false); return; }
    if (trimmed === asset.label) { setEditingLabel(false); return; }
    patch.mutate({ label: trimmed }, { onSuccess: () => setEditingLabel(false) });
  };

  return (
    <Card
      className={[
        "overflow-hidden transition-opacity",
        asset.isActive ? "" : "opacity-60",
      ].join(" ")}
      data-testid={`card-asset-${asset.id}`}
    >
      <CardContent className="space-y-2 p-3">
        {/* Header row */}
        <div className="flex items-start gap-2">
          <div className="flex flex-1 min-w-0 flex-col gap-0.5">
            {editingLabel ? (
              <form
                onSubmit={(e) => { e.preventDefault(); saveLabel(); }}
                className="flex items-center gap-1"
              >
                <Input
                  ref={labelInputRef}
                  value={labelValue}
                  onChange={(e) => setLabelValue(e.target.value)}
                  className="h-7 text-xs"
                  autoFocus
                  maxLength={120}
                  data-testid={`input-asset-label-${asset.id}`}
                />
                <Button type="submit" size="sm" className="h-7 px-2 text-xs" disabled={patch.isPending}>
                  {patch.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => { setLabelValue(asset.label); setEditingLabel(false); }}
                >
                  Cancel
                </Button>
              </form>
            ) : (
              <button
                type="button"
                className="truncate text-left text-sm font-medium hover:underline"
                onClick={() => { if (!asset.archivedAt) setEditingLabel(true); }}
                title="Click to edit label"
                data-testid={`button-edit-label-${asset.id}`}
              >
                {asset.label}
              </button>
            )}
            <div className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
              <span className="font-mono">{asset.fileName}</span>
              <span>·</span>
              <span>{asset.format.toUpperCase()}</span>
              {asset.sizeBytes ? <><span>·</span><span>{formatBytes(asset.sizeBytes)}</span></> : null}
              {asset.kind === "music" && asset.durationSec
                ? <><span>·</span><span>{formatDuration(asset.durationSec)}</span></>
                : null}
              {asset.kind === "font" && asset.fontFamily
                ? <><span>·</span><span className="font-mono">{asset.fontFamily}</span></>
                : null}
            </div>
            <CopyIdButton id={asset.id} />
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <Badge
              variant={asset.isActive ? "default" : "outline"}
              className="text-[10px] px-1.5 py-0"
              data-testid={`badge-asset-active-${asset.id}`}
            >
              {asset.isActive ? "Active" : "Inactive"}
            </Badge>
            <Switch
              checked={asset.isActive}
              onCheckedChange={(v) => patch.mutate({ isActive: v })}
              disabled={patch.isPending || !!asset.archivedAt}
              aria-label={asset.isActive ? "Deactivate asset" : "Activate asset"}
              data-testid={`switch-asset-active-${asset.id}`}
            />
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              aria-label={`Archive ${asset.label}`}
              onClick={() => onArchive(asset.id)}
              disabled={archiving || !!asset.archivedAt}
              data-testid={`button-archive-asset-${asset.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Preview */}
        {asset.kind === "font" ? (
          <FontSpecimen asset={asset} />
        ) : (
          <AudioPreview asset={asset} />
        )}

        {asset.sha256 && (
          <p className="truncate font-mono text-[9px] text-muted-foreground" title={asset.sha256}>
            sha256: {asset.sha256.slice(0, 16)}…
          </p>
        )}
        {asset.archivedAt && (
          <p className="text-[10px] text-destructive/80">
            Archived {new Date(asset.archivedAt).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function FontUrlImportForm() {
  const { toast } = useToast();
  const [mode, setMode] = useState<"url" | "google">("url");
  const [label, setLabel] = useState("");
  const [fontUrl, setFontUrl] = useState("");
  const [googleFamily, setGoogleFamily] = useState("");
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const importFont = useMutation({
    mutationFn: async () => {
      if (mode === "google" && !googleFamily) throw new Error("Choose a Google Font");
      if (mode === "url" && !label.trim()) throw new Error("Enter a display label");
      if (mode === "url" && !fontUrl.trim()) throw new Error("Enter a direct public HTTPS font URL");
      const res = await fetch(`${ASSETS_KEY}/${mode === "google" ? "import-google-font" : "import-font"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(mode === "google"
          ? { family: googleFamily }
          : { label: label.trim(), fontUrl: fontUrl.trim() }),
        credentials: "include",
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({ message: "Font import failed" }));
        throw new Error(error.message || "Font import failed");
      }
      return res.json() as Promise<{ duplicate?: boolean; message?: string }>;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [ASSETS_KEY] });
      const message = result.message || (result.duplicate
        ? "This font is already in the shared catalog."
        : "Font was validated and copied into the shared catalog.");
      setFeedback({ type: "success", text: message });
      setLabel("");
      setFontUrl("");
      setGoogleFamily("");
      toast({
        title: result.duplicate
          ? "Font already exists"
          : mode === "google" ? "Google Font imported" : "Font imported",
        description: message,
      });
    },
    onError: (error: Error) => {
      setFeedback({ type: "error", text: error.message });
      toast({ title: "Font import failed", description: error.message, variant: "destructive" });
    },
  });

  return (
    <form
      className="flex flex-col gap-3 rounded-xl border border-dashed border-border/60 bg-muted/20 p-4"
      onSubmit={(event) => { event.preventDefault(); setFeedback(null); importFont.mutate(); }}
      data-testid="form-import-font-url"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={mode === "url" ? "secondary" : "ghost"}
          className="h-7 px-2 text-xs"
          onClick={() => { setMode("url"); setFeedback(null); }}
          disabled={importFont.isPending}
          data-testid="button-font-import-mode-url"
        >
          <Globe2 className="mr-1 h-3.5 w-3.5" />
          Import from HTTPS
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === "google" ? "secondary" : "ghost"}
          className="h-7 px-2 text-xs"
          onClick={() => { setMode("google"); setFeedback(null); }}
          disabled={importFont.isPending}
          data-testid="button-font-import-mode-google"
        >
          <Type className="mr-1 h-3.5 w-3.5" />
          Choose from Google Fonts
        </Button>
      </div>
      {mode === "google" ? (
        <>
          <p className="text-xs text-muted-foreground">
            Choose a curated family. Its validated TTF is copied into the shared catalog,
            so generated videos never depend on Google at render time.
          </p>
          <select
            value={googleFamily}
            onChange={(event) => {
              setGoogleFamily(event.target.value);
              setFeedback(null);
            }}
            disabled={importFont.isPending}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            aria-label="Google Font family"
            data-testid="select-import-google-font"
          >
            <option value="">Choose a family…</option>
            {GOOGLE_FONT_CATALOG.map((font) => (
              <option key={font.family} value={font.family}>
                {font.family} · {font.category}
              </option>
            ))}
          </select>
        </>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            Use a direct, publicly reachable HTTPS URL ending in <code>.ttf</code> or <code>.otf</code>.
            The file is checked and copied into the shared catalog; URLs with credentials, private targets,
            unreadable fonts, redirects to unsafe hosts, or files over 5 MB are rejected.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="vra-font-url-label" className="text-xs">Display label</Label>
              <Input
                id="vra-font-url-label"
                value={label}
                onChange={(event) => { setLabel(event.target.value); setFeedback(null); }}
                placeholder="e.g. Brand Sans Bold"
                maxLength={120}
                className="h-8 text-xs"
                disabled={importFont.isPending}
                data-testid="input-import-font-label"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vra-font-url" className="text-xs">Direct HTTPS font URL</Label>
              <Input
                id="vra-font-url"
                type="url"
                value={fontUrl}
                onChange={(event) => { setFontUrl(event.target.value); setFeedback(null); }}
                placeholder="https://cdn.example.com/brand-sans.ttf"
                maxLength={2048}
                className="h-8 text-xs"
                disabled={importFont.isPending}
                data-testid="input-import-font-url"
              />
            </div>
          </div>
        </>
      )}
      {feedback && (
        <p
          className={feedback.type === "error" ? "text-xs text-destructive" : "text-xs text-green-600 dark:text-green-400"}
          role={feedback.type === "error" ? "alert" : "status"}
          data-testid={`text-import-font-${feedback.type}`}
        >
          {feedback.text}
        </p>
      )}
      <div className="flex justify-end">
        <Button
          type="submit"
          size="sm"
          className="h-8 text-xs"
          disabled={importFont.isPending || (mode === "google"
            ? !googleFamily
            : !label.trim() || !fontUrl.trim())}
          data-testid={mode === "google"
            ? "button-submit-import-google-font"
            : "button-submit-import-font-url"}
        >
          {importFont.isPending ? (
            <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Importing…</>
          ) : (
            mode === "google"
              ? <><Type className="mr-1 h-3.5 w-3.5" /> Add Google Font</>
              : <><Globe2 className="mr-1 h-3.5 w-3.5" /> Import font</>
          )}
        </Button>
      </div>
    </form>
  );
}

function UploadForm({ kind }: { kind: "music" }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const accept = ".mp3,.wav,audio/mpeg,audio/wav";

  const upload = useMutation({
    mutationFn: async () => {
      if (!selectedFile) throw new Error("Select a file first");
      if (!label.trim()) throw new Error("Enter a label");
      const fd = new FormData();
      fd.append("asset", selectedFile);
      fd.append("kind", kind);
      fd.append("label", label.trim());
      const res = await fetch(`${ASSETS_KEY}/import`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const msg = await res.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(msg.message || "Upload failed");
      }
      return res.json();
    },
    onSuccess: (result: { duplicate?: boolean; message?: string }) => {
      queryClient.invalidateQueries({ queryKey: [ASSETS_KEY] });
      setLabel("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast({
        title: result.duplicate
          ? "Asset already exists"
          : "Music uploaded",
        description: result.message,
      });
    },
    onError: (e: Error) => {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    },
  });

  return (
    <form
      className="flex flex-col gap-3 rounded-xl border border-dashed border-border/60 bg-muted/20 p-4"
      onSubmit={(e) => { e.preventDefault(); upload.mutate(); }}
      data-testid={`form-upload-${kind}`}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <Upload className="h-4 w-4" />
        Upload music track
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`vra-label-${kind}`} className="text-xs">Label</Label>
          <Input
            id={`vra-label-${kind}`}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Upbeat Summer"
            maxLength={120}
            className="h-8 text-xs"
            disabled={upload.isPending}
            data-testid={`input-upload-label-${kind}`}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">
            File{" "}
            <span className="text-muted-foreground">
              (MP3 / WAV)
            </span>
          </Label>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              className="hidden"
              disabled={upload.isPending}
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              data-testid={`input-upload-file-${kind}`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending}
            >
              {selectedFile ? selectedFile.name : "Choose file…"}
            </Button>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          type="submit"
          size="sm"
          className="h-8 text-xs"
          disabled={!label.trim() || !selectedFile || upload.isPending}
          data-testid={`button-submit-upload-${kind}`}
        >
          {upload.isPending ? (
            <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Uploading…</>
          ) : (
            <><Upload className="mr-1 h-3.5 w-3.5" /> Upload</>
          )}
        </Button>
      </div>
    </form>
  );
}

function AssetKindPanel({ kind, assets }: { kind: "font" | "music"; assets: VideoReferenceAsset[] }) {
  const { toast } = useToast();
  const [archiveTarget, setArchiveTarget] = useState<string | null>(null);

  const archive = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `${ASSETS_KEY}/${id}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ASSETS_KEY] });
      setArchiveTarget(null);
      toast({ title: "Asset archived" });
    },
    onError: (e: Error) => {
      toast({ title: "Archive failed", description: e.message, variant: "destructive" });
    },
  });

  const kindAssets = assets.filter((a) => a.kind === kind);
  const active = kindAssets.filter((a) => a.isActive);
  const inactive = kindAssets.filter((a) => !a.isActive);

  return (
    <div className="space-y-4">
      {kind === "music" ? <UploadForm kind="music" /> : null}

      {kindAssets.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`text-no-assets-${kind}`}>
          {kind === "font"
            ? "No snapshotted fonts yet. Google Fonts are prepared automatically when selected in Video Reference Ads."
            : "No music assets uploaded yet."}
        </p>
      ) : (
        <div className="space-y-4">
          {active.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Active ({active.length})
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {active.map((a) => (
                  <AssetCard
                    key={a.id}
                    asset={a}
                    onArchive={setArchiveTarget}
                    archiving={archive.isPending && archive.variables === a.id}
                  />
                ))}
              </div>
            </div>
          )}
          {inactive.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Inactive / Archived ({inactive.length})
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {inactive.map((a) => (
                  <AssetCard
                    key={a.id}
                    asset={a}
                    onArchive={setArchiveTarget}
                    archiving={archive.isPending && archive.variables === a.id}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <AlertDialog
        open={!!archiveTarget}
        onOpenChange={(open) => { if (!open && !archive.isPending) setArchiveTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this asset?</AlertDialogTitle>
            <AlertDialogDescription>
              The asset will be deactivated and hidden from new runs. Existing runs that already
              reference it are not affected. Archived catalog objects are retained for those runs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archive.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (archiveTarget) archive.mutate(archiveTarget);
              }}
              disabled={!archiveTarget || archive.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-archive-asset"
            >
              {archive.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Archiving…</>
              ) : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function VideoReferenceAssetsTab() {
  const { data: assets = [], isLoading, error, refetch, isFetching } = useQuery<VideoReferenceAsset[]>({
    queryKey: [ASSETS_KEY],
    queryFn: async () => {
      const res = await apiRequest("GET", ASSETS_KEY);
      return res.json();
    },
  });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-medium" data-testid="text-vra-title">Video Reference Ads Settings</h2>
        <p className="text-sm text-muted-foreground">
          Enable brands and optionally add Sim fallback routing while
          managing shared fonts and music. Reference-video templates are managed from the Video
          Templates page in each brand's sidebar; prompts are edited on the Video Reference Ads page.
        </p>
      </div>

      <div className="space-y-4 border-t pt-5">
        <div>
          <h3 className="text-base font-medium">Shared assets</h3>
          <p className="text-sm text-muted-foreground">
            Active assets appear in the font and music pickers on the generation page.
          </p>
        </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading assets…
        </div>
      ) : error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm space-y-2">
          <p className="font-medium text-destructive">Failed to load assets</p>
          <p className="text-xs font-mono text-muted-foreground">{(error as Error).message}</p>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Retrying…" : "Retry"}
          </Button>
        </div>
      ) : (
        <Tabs defaultValue="fonts">
          <TabsList className="mb-4" data-testid="tabs-vra-kinds">
            <TabsTrigger value="fonts" data-testid="tab-vra-fonts">
              <Type className="mr-1.5 h-4 w-4" />
              Fonts
              <Badge variant="secondary" className="ml-2 text-[10px] px-1.5">
                {assets.filter((a) => a.kind === "font" && a.isActive).length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="music" data-testid="tab-vra-music">
              <Music className="mr-1.5 h-4 w-4" />
              Music
              <Badge variant="secondary" className="ml-2 text-[10px] px-1.5">
                {assets.filter((a) => a.kind === "music" && a.isActive).length}
              </Badge>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="fonts">
            <AssetKindPanel kind="font" assets={assets} />
          </TabsContent>
          <TabsContent value="music">
            <AssetKindPanel kind="music" assets={assets} />
          </TabsContent>
        </Tabs>
      )}
      </div>

      <div className="border-t pt-5">
        <WorkflowSettingsPanel />
      </div>
    </div>
  );
}
